import type { PolicyStatement } from ':helpers/casl/abac/policy.types';

import { AccessLevel } from ':helpers/casl/abac/access-levels';
import { ORG_FROM_RESTAURANT_SCOPE_PLACEHOLDER, RESTAURANT_SCOPE_PLACEHOLDER } from ':helpers/casl/abac/policy-scope';
import { CaslAction, CaslSubject } from ':helpers/casl/casl.enum';
import { PostSource } from ':modules/posts/social-posts.interface';

/**
 * Maps the role-editor UI (section → sub-section → level) to the statements stored on a role.
 * A chosen level expands to a statement bundle via getStatementsForLevel; actions are the real audited check actions.
 * TODO(roles): feedback postType needs feedback.controller wiring; still missing the TEMPLATE / SEND_VERIFICATION_EMAIL splits, GALLERY/Stories/Messages, and PUBLISH_SEO.
 */

export enum SectionName {
    SEO = 'seo',
    SOCIAL = 'social',
    E_REPUTATION = 'e_reputation',
    INTERACTIONS = 'interactions',
    RESOURCES = 'resources',
    SETTINGS = 'settings',
}

export enum SubSectionName {
    INFORMATIONS = 'informations',
    SEO_POSTS = 'seo_posts',
    INSPIRATIONS = 'inspirations',
    SOCIAL_POSTS = 'social_posts',
    REVIEWS = 'reviews',
    CAMPAIGNS = 'campaigns',
    COMMENTS = 'comments',
    KEYWORDS = 'keywords',
    CLIENTS = 'clients',
    PLATFORMS = 'platforms',
    USERS = 'users',
}

type LevelStatements = Omit<PolicyStatement, 'effect'>;

export interface SubSectionDefinition {
    subSection: SubSectionName;
    /** Ordered, cumulative: each level's statements should be a superset of the previous level's. */
    levels: { level: AccessLevel; statements: LevelStatements[] }[];
}

export interface SectionDefinition {
    section: SectionName;
    subSections: SubSectionDefinition[];
}

// Condition helpers — restaurant subjects scope on `restaurantId`, except Restaurant itself on `_id`.
const onRestaurant: Record<string, unknown> = { restaurantId: RESTAURANT_SCOPE_PLACEHOLDER };
const onRestaurantId: Record<string, unknown> = { _id: RESTAURANT_SCOPE_PLACEHOLDER };
const onOrgFromRestaurant: Record<string, unknown> = { organizationIds: ORG_FROM_RESTAURANT_SCOPE_PLACEHOLDER };
// Feedback is scoped both to the restaurant and to the type of post it belongs to (seo vs social).
// The check site (feedback.controller) must pass `postType`, derived from the post that references the feedback.
const onRestaurantSeoFeedback: Record<string, unknown> = { restaurantId: RESTAURANT_SCOPE_PLACEHOLDER, postType: PostSource.SEO };
const onRestaurantSocialFeedback: Record<string, unknown> = { restaurantId: RESTAURANT_SCOPE_PLACEHOLDER, postType: PostSource.SOCIAL };

export const PERMISSION_CATALOGUE: SectionDefinition[] = [
    {
        section: SectionName.SEO,
        subSections: [
            {
                subSection: SubSectionName.INFORMATIONS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [{ action: [CaslAction.UPDATE], subject: CaslSubject.RESTAURANT, conditions: onRestaurantId }],
                    },
                ],
            },
            {
                // EDIT: manage SEO posts + their feedback. The publish levels are not available yet (pending).
                subSection: SubSectionName.SEO_POSTS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [
                            { action: [CaslAction.MANAGE], subject: CaslSubject.POST, conditions: onRestaurant },
                            { action: [CaslAction.MANAGE], subject: CaslSubject.SEO_POST, conditions: onRestaurant },
                            { action: [CaslAction.ANSWER], subject: CaslSubject.FEEDBACK, conditions: onRestaurantSeoFeedback },
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.FEEDBACK,
                                fields: ['isOpen'],
                                conditions: onRestaurantSeoFeedback,
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        section: SectionName.SOCIAL,
        subSections: [
            {
                subSection: SubSectionName.INSPIRATIONS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.PLATFORM,
                                fields: ['watchedAccounts'],
                                conditions: onRestaurant,
                            },
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.RESTAURANT,
                                fields: ['bookmarkedPosts'],
                                conditions: onRestaurantId,
                            },
                        ],
                    },
                ],
            },
            {
                // EDIT: manage social posts + their feedback. VALIDATE: also submit for validation. PUBLISH: also publish directly.
                subSection: SubSectionName.SOCIAL_POSTS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [
                            { action: [CaslAction.MANAGE], subject: CaslSubject.POST, conditions: onRestaurant },
                            { action: [CaslAction.MANAGE], subject: CaslSubject.SOCIAL_POST, conditions: onRestaurant },
                            { action: [CaslAction.ANSWER], subject: CaslSubject.FEEDBACK, conditions: onRestaurantSocialFeedback },
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.FEEDBACK,
                                fields: ['isOpen'],
                                conditions: onRestaurantSocialFeedback,
                            },
                        ],
                    },
                    {
                        level: AccessLevel.VALIDATE,
                        statements: [
                            { action: [CaslAction.MANAGE], subject: CaslSubject.POST, conditions: onRestaurant },
                            { action: [CaslAction.MANAGE], subject: CaslSubject.SOCIAL_POST, conditions: onRestaurant },
                            { action: [CaslAction.ANSWER], subject: CaslSubject.FEEDBACK, conditions: onRestaurantSocialFeedback },
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.FEEDBACK,
                                fields: ['isOpen'],
                                conditions: onRestaurantSocialFeedback,
                            },
                            { action: [CaslAction.PUBLISH], subject: CaslSubject.SOCIAL_POST, conditions: onRestaurant },
                        ],
                    },
                    {
                        level: AccessLevel.PUBLISH,
                        statements: [
                            { action: [CaslAction.MANAGE], subject: CaslSubject.POST, conditions: onRestaurant },
                            { action: [CaslAction.MANAGE], subject: CaslSubject.SOCIAL_POST, conditions: onRestaurant },
                            { action: [CaslAction.ANSWER], subject: CaslSubject.FEEDBACK, conditions: onRestaurantSocialFeedback },
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.FEEDBACK,
                                fields: ['isOpen'],
                                conditions: onRestaurantSocialFeedback,
                            },
                            { action: [CaslAction.PUBLISH], subject: CaslSubject.SOCIAL_POST, conditions: onRestaurant },
                            { action: [CaslAction.PUBLISH_WITHOUT_VALIDATION], subject: CaslSubject.SOCIAL_POST, conditions: onRestaurant },
                        ],
                    },
                ],
            },
        ],
    },
    {
        section: SectionName.E_REPUTATION,
        subSections: [
            {
                subSection: SubSectionName.REVIEWS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    // VALIDATE: manage replies (checked on UPDATE), submitted for validation.
                    {
                        level: AccessLevel.VALIDATE,
                        statements: [{ action: [CaslAction.UPDATE], subject: CaslSubject.REVIEW, conditions: onRestaurant }],
                    },
                    // PUBLISH: also publish replies directly, bypassing validation.
                    {
                        level: AccessLevel.PUBLISH,
                        statements: [
                            { action: [CaslAction.UPDATE], subject: CaslSubject.REVIEW, conditions: onRestaurant },
                            { action: [CaslAction.PUBLISH_WITHOUT_VALIDATION], subject: CaslSubject.REVIEW, conditions: onRestaurant },
                        ],
                    },
                ],
            },
            {
                subSection: SubSectionName.CAMPAIGNS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [{ action: [CaslAction.MANAGE], subject: CaslSubject.CAMPAIGN, conditions: onRestaurant }],
                    },
                ],
            },
        ],
    },
    {
        section: SectionName.INTERACTIONS,
        subSections: [
            {
                subSection: SubSectionName.COMMENTS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [{ action: [CaslAction.UPDATE], subject: CaslSubject.COMMENT, conditions: onRestaurant }],
                    },
                ],
            },
        ],
    },
    {
        section: SectionName.RESOURCES,
        subSections: [
            {
                subSection: SubSectionName.KEYWORDS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [
                            { action: [CaslAction.MANAGE], subject: CaslSubject.KEYWORD, conditions: onRestaurant },
                            {
                                action: [CaslAction.UPDATE],
                                subject: CaslSubject.RESTAURANT,
                                fields: ['bricks'],
                                conditions: onRestaurantId,
                            },
                        ],
                    },
                ],
            },
            {
                subSection: SubSectionName.CLIENTS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [{ action: [CaslAction.MANAGE], subject: CaslSubject.CLIENT, conditions: onRestaurant }],
                    },
                ],
            },
        ],
    },
    {
        section: SectionName.SETTINGS,
        subSections: [
            {
                // EDIT: full platform management (connect/disconnect + update = the current MANAGE + UPDATE); no intermediate tier.
                subSection: SubSectionName.PLATFORMS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.EDIT,
                        statements: [{ action: [CaslAction.MANAGE], subject: CaslSubject.PLATFORM, conditions: onRestaurant }],
                    },
                ],
            },
            {
                subSection: SubSectionName.USERS,
                levels: [
                    { level: AccessLevel.NONE, statements: [] },
                    {
                        level: AccessLevel.READ,
                        statements: [{ action: [CaslAction.READ], subject: CaslSubject.USER_RESTAURANT, conditions: onRestaurant }],
                    },
                    {
                        // EDIT carries full user management: the restaurant membership (USER_RESTAURANT) plus the org-level user record it belongs to.
                        // USER_RESTAURANT actions are restaurant-scoped; USER actions are scoped to the org derived from the restaurant.
                        level: AccessLevel.EDIT,
                        statements: [
                            { action: [CaslAction.READ], subject: CaslSubject.USER_RESTAURANT, conditions: onRestaurant },
                            { action: [CaslAction.CREATE], subject: CaslSubject.USER_RESTAURANT, conditions: onRestaurant },
                            { action: [CaslAction.UPDATE], subject: CaslSubject.USER_RESTAURANT, conditions: onRestaurant },
                            { action: [CaslAction.DELETE], subject: CaslSubject.USER_RESTAURANT, conditions: onRestaurant },
                            { action: [CaslAction.CREATE], subject: CaslSubject.USER, conditions: onOrgFromRestaurant },
                            { action: [CaslAction.DELETE], subject: CaslSubject.USER, conditions: onOrgFromRestaurant },
                            {
                                action: [CaslAction.MANAGE],
                                subject: CaslSubject.USER,
                                fields: ['caslRole'],
                                conditions: onOrgFromRestaurant,
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

/** Returns the statements bundle for a (sub-section, level), or [] if the level is not defined. */
export function getStatementsForLevel(subSection: SubSectionName, level: AccessLevel): LevelStatements[] {
    for (const section of PERMISSION_CATALOGUE) {
        const found = section.subSections.find((s) => s.subSection === subSection);
        if (found) {
            return found.levels.find((l) => l.level === level)?.statements ?? [];
        }
    }
    return [];
}
